import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Computer,
  Download,
  Globe,
  Inbox,
  MinusCircle,
  Monitor,
  Plus,
  RefreshCw,
  Search,
  Wifi,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  DepartmentsResponse,
  Order,
  OrderChannel,
  OrderListSortBy,
  OrderStatus,
  PaymentRail,
  PendingPaymentRow,
  SortDir,
} from "@tilt/pos-sdk";

import { CancelPaymentDialog } from "../components/sale/CancelPaymentDialog";
import { OrderDetailView } from "../components/sale/OrderDetailView";
import { TimelinePaymentDrawer } from "../components/sale/TimelinePaymentDrawer";
import { LocationSubsetPicker } from "../components/scope/LocationSubsetPicker";

import { usePageTitle } from "../lib/use-page-title";

import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { DescriptionDetails } from "../components/ui/DescriptionDetails";
import { EmptyState } from "../components/ui/EmptyState";
import { TextInput } from "../components/ui/Field";
import { InlineSpinner, PageSpinner } from "../components/ui/Spinner";
import { OrderStatusBadge, PaymentStatusBadge } from "../components/ui/StatusBadge";
import { getPosClient } from "../lib/api";
import { formatCents, formatDateTime } from "../lib/format";
import { lookupMethodByTypeSubtype, resolveMethodIcon } from "../lib/payment-methods";
import { useDarkMode } from "../lib/use-dark-mode";
import { useActiveTimezone } from "../lib/useActiveTimezone";
import { orderStatusLabelKey } from "../lib/orderStatus";
import {
  POS_TRANSACTION_VIEW,
  POS_TRANSACTION_VIEW_ALL_CHANNELS,
  POS_TRANSACTION_VIEW_OWN,
} from "../lib/permissions";
import { cn } from "../lib/utils";

const STATUS_OPTIONS: readonly (OrderStatus | "all")[] = [
  "all",
  "open",
  "partially_paid",
  "paid",
  "voided",
  "partially_refunded",
  "refunded",
];

const PAGE_SIZE = 50;

/** Transactions list - ordered DESC by latest_payment_at (V1 parity), scope-narrowed by
 *  the API based on the caller's permissions. Cashier with only
 *  ``view_own`` sees their own; supervisor and above see the
 *  location / org / partner rollup as appropriate.
 *
 *  Scalability:
 *    * Server-side pagination via `limit + offset` (PAGE_SIZE rows per
 *      hit). API returns `total` and `has_more` so we can render a
 *      pager without a separate count call.
 *    * Search runs through the V0043 trigram GIN index - substring
 *      matches stay sub-50ms even at six-digit row counts.
 *    * `placeholderData` keeps the previous page visible while the
 *      next loads, so the page never flickers blank during nav.
 *    * 300ms search debounce caps API hits at 3/s while the user
 *      types. */
export function TransactionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle("transactions");

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  const [activeTab, setActiveTab] = useState<"orders" | "pending">("orders");
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  // Sort state - server-side via the new ``sort_by`` + ``sort_dir``
  // query params (#227 backend). Default matches the prior page
  // behavior (created_at DESC). Clicking a sortable header toggles
  // direction within the same column; clicking a different column
  // resets direction to that column's natural default.
  const [sortBy, setSortBy] = useState<OrderListSortBy>("latest_payment_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // V1-parity filters - Department dropdown, Payment Type dropdown,
  // Exceptions Only toggle. Backend support landed in tilt-pos-api
  // PR #100; the UI exposes them when the active scope makes them
  // meaningful (Department picker only when we know which location's
  // catalog to load).
  const [departmentId, setDepartmentId] = useState<string>("");
  const [paymentType, setPaymentType] = useState<string>("");
  const [exceptionsOnly, setExceptionsOnly] = useState<boolean>(false);
  const [discountOnly, setDiscountOnly] = useState<boolean>(false);
  const [channelFilter, setChannelFilter] = useState<OrderChannel | "">("");

  // 300ms debounce on the search box. useEffect (not useMemo) so the
  // cleanup actually runs and clears the timer when the user keeps
  // typing.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Active membership drives both the Department picker (we load the
  // catalog of the active location) and the location-subset
  // multi-select for org/partner-scoped operators.
  const activeMembership = profile.data?.active_membership ?? null;
  const activeLocationId = activeMembership?.location?.id ?? null;
  const scopeKind = activeMembership?.scope_kind ?? "location";
  // Org / partner / platform-scoped operators may want a consolidated
  // rollup OR a picked subset of their visible locations. Empty list
  // == consolidated. Picker self-hides for location-scoped (single row).
  const [locationIds, setLocationIds] = useState<ReadonlyArray<string>>([]);
  const showSubsetPicker = scopeKind !== "location";

  const railsQuery = useQuery({
    enabled: activeLocationId != null,
    queryKey: ["payment-rails", activeLocationId],
    queryFn: () => getPosClient().getLocationPaymentRails(activeLocationId!),
    staleTime: 5 * 60_000,
  });
  const uniqueRailTypes = useMemo<ReadonlyArray<PaymentRail>>(() => {
    const seen = new Set<string>();
    return (railsQuery.data?.rails ?? [])
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .filter((r) => {
        if (seen.has(r.payment_type)) return false;
        seen.add(r.payment_type);
        return true;
      });
  }, [railsQuery.data?.rails]);

  // Reset to first page whenever filters / sort change - without this
  // an active page > 1 stays positioned on a now-mismatched offset and
  // the page renders blank or wrong rows after the next fetch.
  useEffect(() => {
    setOffset(0);
  }, [
    statusFilter,
    sortBy,
    sortDir,
    departmentId,
    paymentType,
    exceptionsOnly,
    discountOnly,
    locationIds,
    channelFilter,
  ]);

  // Departments at the active location - only fetched when we have a
  // location-scoped active membership; org/partner-scope skips this
  // until task #232 ships the cross-location picker.
  const departments = useQuery<DepartmentsResponse | null>({
    queryKey: ["pos", "departments", activeLocationId],
    queryFn: () =>
      activeLocationId
        ? getPosClient().listLocationDepartments(activeLocationId)
        : Promise.resolve(null),
    enabled: !!activeLocationId,
    staleTime: 60_000,
  });

  const orders = useQuery({
    queryKey: [
      "orders",
      {
        statusFilter,
        search: debouncedSearch,
        offset,
        sortBy,
        sortDir,
        departmentId,
        paymentType,
        exceptionsOnly,
        discountOnly,
        channelFilter,
        locationIds: [...locationIds].sort().join(","),
      },
    ],
    queryFn: () =>
      getPosClient().listOrders({
        limit: PAGE_SIZE,
        offset,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        ...(departmentId ? { department_id: departmentId } : {}),
        ...(paymentType ? { payment_type: paymentType } : {}),
        ...(exceptionsOnly ? { exceptions_only: true } : {}),
        ...(discountOnly ? { discount_only: true } : {}),
        ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
        ...(channelFilter ? { channel: channelFilter } : {}),
      }),
    // Keep the previous page visible while the next fetches - no
    // mid-paginate flash of empty + spinner.
    placeholderData: (prev) => prev,
    staleTime: 5_000,
  });

  // Click handler for the sortable column headers. Same column =
  // toggle dir; different column = jump to that column with its
  // natural default direction (DESC for time/amount, ASC for
  // alphabetic - same defaults the API picks if we omit ``sort_dir``,
  // but we apply them client-side too so the chevron matches what's
  // about to come back from the server).
  const naturalDir = (col: OrderListSortBy): SortDir =>
    col === "external_reference" || col === "customer_name" || col === "status" ? "asc" : "desc";
  // Both date columns default to DESC (most-recent first). created_at is the
  // order-open time; latest_payment_at is the most-recent payment attempt.
  // naturalDir already returns "desc" for non-alpha columns, so no override
  // needed - the declaration above covers both "created_at" and "latest_payment_at".
  const onSort = (col: OrderListSortBy) => {
    if (col === sortBy) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir(naturalDir(col));
    }
  };

  if (profile.isPending) {
    return <PageSpinner label={t("common.loading")} />;
  }

  const perms = new Set(profile.data?.permissions ?? []);
  if (!perms.has(POS_TRANSACTION_VIEW) && !perms.has(POS_TRANSACTION_VIEW_OWN)) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("transactions.noPermissionTitle")}>
          {t("transactions.noPermissionBody")}
        </Alert>
      </div>
    );
  }

  return (
    // No max-width - Transactions is a wide table page (Time / Ref /
    // Customer / Total / Paid / Status). The 1280-cap squashed columns
    // on a 1600-wide layout (2026-05-03 dogfood); let the table breathe
    // out to the AppShell's content cap (1600px).
    <div className="space-y-6 pb-12">
      {/* Page header - kicker (small caps eyebrow) above a bold
          tracking-tight title. Same pattern as SalePage so the app
          reads as one coherent surface across pages. */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {t("transactions.kicker")}
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-strong sm:text-3xl xl:text-4xl">
            {t("transactions.title")}
          </h1>
          <p className="mt-1 text-sm text-muted">{t("transactions.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {showSubsetPicker && (
            <LocationSubsetPicker
              value={locationIds}
              onChange={setLocationIds}
              disabled={orders.isFetching}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => orders.refetch()}
            disabled={orders.isFetching}
            leftIcon={
              orders.isFetching ? (
                <InlineSpinner />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              )
            }
            title={t("transactions.refresh")}
          >
            {t("transactions.refresh")}
          </Button>
          <Button onClick={() => navigate("/sale")} leftIcon={<Plus className="h-4 w-4" />}>
            {t("transactions.newSale")}
          </Button>
        </div>
      </header>

      {/* Tab switcher - Orders / Open Payments */}
      <div className="flex gap-1 rounded-lg bg-surface-muted p-1 w-fit">
        <button
          type="button"
          onClick={() => setActiveTab("orders")}
          className={cn(
            "rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
            activeTab === "orders"
              ? "bg-surface text-strong shadow-elev-1"
              : "text-muted hover:text-foreground",
          )}
        >
          {t("transactions.tabOrders")}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("pending")}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
            activeTab === "pending"
              ? "bg-surface text-strong shadow-elev-1"
              : "text-muted hover:text-foreground",
          )}
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          {t("transactions.tabPending")}
        </button>
      </div>

      {activeTab === "pending" && (
        <PendingPaymentsView
          locationIds={locationIds}
          activeLocationId={activeLocationId}
          scopeKind={scopeKind}
        />
      )}

      {activeTab === "orders" && (
        <>
          {/* Summary strip - counts + dollar totals across the FILTERED
          set, not just the current page. Reads the API's summary
          field (#147). Hidden until first results land so it doesn't
          flash zeros while the page initially mounts. */}
          {orders.data && orders.data.summary.order_count > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <SummaryStat
                label={t("transactions.summaryOrders")}
                value={String(orders.data.summary.order_count)}
              />
              <SummaryStat
                label={t("transactions.summaryGross")}
                value={formatCents(orders.data.summary.paid_cents, "USD")}
              />
              <SummaryStat
                label={t("transactions.summaryDiscounts")}
                value={
                  orders.data.summary.discount_cents > 0
                    ? `-${formatCents(orders.data.summary.discount_cents, "USD")}`
                    : formatCents(0, "USD")
                }
                {...(orders.data.summary.discount_cents > 0 ? { tone: "emerald" as const } : {})}
              />
              <SummaryStat
                label={t("transactions.summaryRefunded")}
                value={formatCents(orders.data.summary.refunded_cents, "USD")}
                {...(orders.data.summary.refunded_cents > 0 ? { tone: "warn" as const } : {})}
              />
              <SummaryStat
                label={t("transactions.summaryNet")}
                value={formatCents(orders.data.summary.net_cents, "USD")}
                tone="emerald"
              />
            </div>
          )}

          {/* Filter strip - promoted out of the table card's header so it
          breathes on its own. Soft elev-1 surface, no border lines.
          Stacks vertically on phone, single row on tablet+. */}
          <div className="flex flex-col gap-3 rounded-xl bg-surface p-4 shadow-elev-1 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
                  aria-hidden="true"
                />
                <TextInput
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("transactions.searchPlaceholder")}
                  className="min-h-tap w-full pl-9 sm:w-72"
                />
              </div>
              {/* Status filter - appearance-none strips the native chevron,
              so we render our own ChevronDown overlay (right padding
              reserves room for it) so the control still reads as a
              dropdown. Pointer-events-none on the icon lets the click
              pass through to the underlying select. */}
              <div className="relative sm:w-40">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  aria-label={t("transactions.filterByStatus")}
                  className="min-h-tap w-full appearance-none rounded-md bg-surface-muted pl-3 pr-9 text-sm font-medium text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  {STATUS_OPTIONS.map((s) =>
                    s === "all" ? (
                      <option key={s} value={s}>
                        {t("transactions.statusAll")}
                      </option>
                    ) : (
                      <option key={s} value={s}>
                        {t(orderStatusLabelKey(s))}
                      </option>
                    ),
                  )}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
                  aria-hidden="true"
                />
              </div>
              {/* Department filter - V1 parity. Loaded from the active
              location's catalog (org/partner-scope picker is a
              follow-up on task #232). */}
              {departments.data && departments.data.departments.length > 0 && (
                <div className="relative sm:w-44">
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    aria-label={t("transactions.filterByDepartment")}
                    className="min-h-tap w-full appearance-none rounded-md bg-surface-muted pl-3 pr-9 text-sm font-medium text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <option value="">{t("transactions.allDepartments")}</option>
                    {departments.data.departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
                    aria-hidden="true"
                  />
                </div>
              )}
              {/* Payment Type filter - V1 parity. Closed allowlist; the
              backend rejects anything else. */}
              <div className="relative sm:w-40">
                <select
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  aria-label={t("transactions.filterByPaymentType")}
                  className="min-h-tap w-full appearance-none rounded-md bg-surface-muted pl-3 pr-9 text-sm font-medium text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="">{t("transactions.allPaymentTypes")}</option>
                  {uniqueRailTypes.map((r) => (
                    <option key={r.payment_type} value={r.payment_type}>
                      {t(`transactions.paymentType.${r.payment_type}`, {
                        defaultValue: r.display_label_en,
                      })}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
                  aria-hidden="true"
                />
              </div>
              {/* Exceptions Only - V1 toggle that narrows to non-paid
              orders (open / partially_paid / partially_refunded /
              refunded / voided). Useful at end-of-shift when the
              cashier is reconciling. */}
              <label className="inline-flex min-h-tap cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={exceptionsOnly}
                  onChange={(e) => setExceptionsOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-line text-brand-primary focus:ring-brand-primary"
                />
                <span className="text-sm text-foreground">{t("transactions.exceptionsOnly")}</span>
              </label>
              {/* With discount only - narrows to orders where a cashier
              applied a give-away. Useful for end-of-day reconciliation. */}
              <label className="inline-flex min-h-tap cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={discountOnly}
                  onChange={(e) => setDiscountOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-line text-brand-primary focus:ring-brand-primary"
                />
                <span className="text-sm text-foreground">
                  {t("transactions.filterOnlyDiscount")}
                </span>
              </label>
              {/* Channel filter - only visible for supervisors / operators
              who hold pos.transaction.view_all_channels. Cashiers are
              already scoped to their own location's POS orders. */}
              {perms.has(POS_TRANSACTION_VIEW_ALL_CHANNELS) && (
                <div className="relative sm:w-40">
                  <select
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value as OrderChannel | "")}
                    aria-label={t("transactions.filterByChannel")}
                    className="min-h-tap w-full appearance-none rounded-md bg-surface-muted pl-3 pr-9 text-sm font-medium text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <option value="">{t("transactions.allChannels")}</option>
                    <option value="pos">{t("transactions.channel.pos")}</option>
                    <option value="kiosk">{t("transactions.channel.kiosk")}</option>
                    <option value="m2m">{t("transactions.channel.m2m")}</option>
                    <option value="online">{t("transactions.channel.online")}</option>
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {/* Export CSV - current page only (limit rows) for
            back-office reconciliation. The pager already shows
            "Page X of N" so the operator knows what slice they're
            exporting. Multi-page export is a follow-up. */}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Download className="h-4 w-4" />}
              disabled={!orders.data || orders.data.orders.length === 0}
              onClick={() => downloadCsv(orders.data?.orders ?? [])}
              className="min-h-tap"
            >
              {t("transactions.exportCsv")}
            </Button>
          </div>

          {/* Table card - pure data surface. No header chrome (filters
          live above), zero internal padding so the table edge-to-edge
          fills the card; rows get their own breathing room from the
          cell padding. */}
          <section
            className="overflow-hidden rounded-xl bg-surface shadow-elev-1"
            aria-label={t("transactions.title")}
          >
            {orders.isPending && !orders.data ? (
              <div className="px-5 py-8 sm:px-6">
                <PageSpinner />
              </div>
            ) : orders.isError ? (
              <div className="px-5 py-5 sm:px-6">
                <Alert tone="error">{t("transactions.loadError")}</Alert>
              </div>
            ) : orders.data && orders.data.orders.length === 0 ? (
              <div className="px-5 py-8 sm:px-6">
                <EmptyState
                  icon={<Inbox className="h-8 w-8" />}
                  title={t("transactions.emptyTitle")}
                  body={t("transactions.emptyBody")}
                  action={
                    <Button onClick={() => navigate("/sale")} size="sm">
                      {t("transactions.newSale")}
                    </Button>
                  }
                />
              </div>
            ) : (
              orders.data && (
                <OrderTable
                  orders={orders.data.orders}
                  locationId={activeLocationId}
                  // Row click opens the detail page in a new tab -
                  // matches the inline-expand "Open detail page" link.
                  // Operator dogfood: in a busy day they want to drill
                  // into an order while keeping the filtered list intact
                  // for the next one.
                  onClick={(o) =>
                    window.open(`/transactions/${o.id}`, "_blank", "noopener,noreferrer")
                  }
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  showChannel={perms.has(POS_TRANSACTION_VIEW_ALL_CHANNELS)}
                />
              )
            )}
            {orders.data && orders.data.orders.length > 0 && (
              <Pager
                offset={offset}
                limit={PAGE_SIZE}
                total={orders.data.total}
                hasMore={orders.data.has_more}
                isFetching={orders.isFetching}
                onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                onNext={() => setOffset(offset + PAGE_SIZE)}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "emerald" | "warn";
}) {
  // Soft elev-1 surface - no hard border. Numbers use the
  // monospace token so the four stats line up vertically and the
  // operator can read them as a column at a glance.
  return (
    <div className="rounded-xl bg-surface px-4 py-3 shadow-elev-1">
      <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 font-mono text-xl font-semibold tabular-nums tracking-tight",
          tone === "emerald" && "text-emerald-700 dark:text-emerald-400",
          tone === "warn" && "text-amber-700 dark:text-amber-400",
          !tone && "text-strong",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function csvEscape(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  // Quote if the cell contains comma / quote / newline; double any
  // existing quotes so the CSV stays parseable by Excel + Sheets.
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(orders: readonly Order[]): void {
  const headers = [
    "id",
    "channel",
    "latest_payment_at",
    "created_at",
    "external_reference",
    "customer_name",
    "customer_phone",
    "customer_email",
    "status",
    "total_cents",
    "discount_cents",
    "paid_cents",
    "refunded_cents",
    "currency",
  ];
  const rows = orders.map((o) =>
    [
      o.id,
      o.channel,
      o.latest_payment_at ?? o.created_at,
      o.created_at,
      o.external_reference,
      o.customer_name,
      o.customer_phone,
      o.customer_email,
      o.status,
      o.total_cents,
      o.discount_cents,
      o.paid_cents,
      o.refunded_cents,
      o.currency,
    ]
      .map(csvEscape)
      .join(","),
  );
  const csv = [headers.join(","), ...rows].join("\n");
  // BOM so Excel reads UTF-8 correctly on Windows.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `transactions-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Pager({
  offset,
  limit,
  total,
  hasMore,
  isFetching,
  onPrev,
  onNext,
}: {
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly isFetching: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
}) {
  const { t } = useTranslation();
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  // Pager strip pinned at the bottom of the table card. Its own
  // ``bg-surface-muted`` band acts as a natural separator from the
  // rows above without needing a hard 1px border. Buttons hit the
  // ``min-h-tap`` floor so phone + tablet operators can paginate
  // without missing the target.
  return (
    <div className="flex items-center justify-between gap-3 bg-surface-muted px-5 py-3 text-xs text-muted sm:px-6">
      <div className="flex items-center gap-2 font-mono tabular-nums">
        <span>{t("transactions.pager", { start, end, total })}</span>
        {isFetching && <InlineSpinner className="h-3 w-3 text-faint" />}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrev}
          disabled={offset === 0 || isFetching}
          leftIcon={<ChevronLeft className="h-4 w-4" />}
          className="min-h-tap"
        >
          {t("transactions.prev")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNext}
          disabled={!hasMore || isFetching}
          rightIcon={<ChevronRight className="h-4 w-4" />}
          className="min-h-tap"
        >
          {t("transactions.next")}
        </Button>
      </div>
    </div>
  );
}

function OrderTable({
  orders,
  locationId,
  onClick,
  sortBy,
  sortDir,
  onSort,
  showChannel,
}: {
  readonly orders: readonly Order[];
  readonly locationId: string | null;
  readonly onClick: (o: Order) => void;
  readonly sortBy: OrderListSortBy;
  readonly sortDir: SortDir;
  readonly onSort: (col: OrderListSortBy) => void;
  readonly showChannel: boolean;
}) {
  // Inline expansion - clicking the chevron in the Actions column
  // toggles a details panel below the row showing the order's full
  // payment timeline + customer / dept / surcharge specifics. The
  // chevron's ``stopPropagation`` keeps the row's outer click (which
  // still navigates to ``/transactions/:id``) from also firing, so
  // the operator gets both: a quick inline peek via the chevron AND
  // the dedicated detail page via the row body.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = (id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  // Polished data table:
  //   * header bar is a tinted strip (``bg-surface-muted``) with
  //     uppercase + tracked labels - reads as a clear separator
  //     between header and rows without needing a hard 1px border.
  //   * zebra rows (``odd:bg-surface even:bg-surface-muted/40``) at
  //     low contrast so the stripes read as quiet helpers, not noise.
  //   * amount columns use the ``font-mono`` token + tabular-nums so
  //     digits align vertically across rows - the operator can scan
  //     the column without their eye drifting.
  //   * row hover lifts the contrast slightly (``hover:bg-surface-muted``)
  //     so the click target reads as a real navigation affordance.
  //   * keyboard accessibility - ``<tr>`` isn't natively focusable.
  //     We give each row ``tabIndex=0`` + ``role="button"`` + an
  //     ``aria-label`` describing the order, plus an Enter/Space
  //     handler that mirrors the click. ``focus-visible`` rings the
  //     row in brand-primary so a keyboard-only operator can see
  //     where they are without breaking pointer ergonomics.
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-surface-muted text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          <tr>
            <SortableHeader
              column="created_at"
              label={t("transactions.colDate")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              className="px-5 py-3 sm:px-6"
            />
            <SortableHeader
              column="latest_payment_at"
              label={t("transactions.colTime")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              className="px-3 py-3"
            />
            <SortableHeader
              column="external_reference"
              label={t("transactions.colReference")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              className="px-3 py-3"
            />
            <SortableHeader
              column="customer_name"
              label={t("transactions.colCustomer")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              className="px-3 py-3"
            />
            <th scope="col" className="px-3 py-3">
              {t("transactions.colDepartment")}
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              {t("transactions.colDiscount")}
            </th>
            <SortableHeader
              column="total_cents"
              label={t("sale.totalDue")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              align="right"
              className="px-3 py-3"
            />
            <SortableHeader
              column="paid_cents"
              label={t("sale.paid")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              align="right"
              className="px-3 py-3"
            />
            <th scope="col" className="px-3 py-3 text-right">
              {t("transactions.colRefund")}
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              {t("transactions.colSurcharge")}
            </th>
            <th scope="col" className="px-3 py-3">
              {t("transactions.colPayment")}
            </th>
            {showChannel && (
              <th scope="col" className="px-3 py-3">
                {t("transactions.colChannel")}
              </th>
            )}
            <SortableHeader
              column="status"
              label={t("transactions.colStatus")}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              className="px-3 py-3 text-center"
            />
            <th scope="col" className="px-5 py-3 text-right sm:px-6">
              <span className="sr-only">{t("transactions.colActions")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line/60">
          {orders.map((o, i) => {
            const isExpanded = expandedIds.has(o.id);
            return (
              <Fragment key={o.id}>
                <tr
                  onClick={() => onClick(o)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onClick(o);
                    }
                  }}
                  tabIndex={0}
                  aria-label={t("transactions.rowAria", {
                    reference:
                      o.external_reference ?? `#${o.id.split("-")[0]?.toUpperCase() ?? ""}`,
                    customer: o.customer_name ?? "-",
                    total: formatCents(Math.max(0, o.total_cents - o.discount_cents), o.currency),
                  })}
                  className={cn(
                    "cursor-pointer transition-colors",
                    i % 2 === 1 && "bg-surface-muted/40",
                    "hover:bg-surface-muted",
                    "focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
                  )}
                >
                  <td className="whitespace-nowrap px-5 py-3 font-mono text-xs tabular-nums text-foreground sm:px-6">
                    {formatDateTime(o.created_at, tz)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-xs tabular-nums text-foreground">
                    {o.latest_payment_at ? (
                      formatDateTime(o.latest_payment_at, tz)
                    ) : (
                      <span className="text-faint">-</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-strong">
                    <div className="font-mono text-sm font-medium">
                      {o.external_reference ?? `#${o.id.split("-")[0]?.toUpperCase() ?? ""}`}
                    </div>
                    <DescriptionDetails
                      description={o.description}
                      className="text-xs text-muted"
                    />
                  </td>
                  <td className="px-3 py-3 text-foreground">
                    {o.customer_name ?? <span className="text-faint">-</span>}
                  </td>
                  <td className="px-3 py-3 text-foreground">
                    {o.department_name ?? <span className="text-faint">-</span>}
                  </td>
                  <td
                    className={cn(
                      "whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums",
                      o.discount_cents > 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-faint",
                    )}
                  >
                    {o.discount_cents > 0 ? `-${formatCents(o.discount_cents, o.currency)}` : "-"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono font-semibold tabular-nums text-strong">
                    {formatCents(Math.max(0, o.total_cents - o.discount_cents), o.currency)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums text-foreground">
                    {formatCents(o.paid_cents, o.currency)}
                  </td>
                  <td
                    className={cn(
                      "whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums",
                      o.refunded_cents > 0 ? "text-amber-700 dark:text-amber-400" : "text-faint",
                    )}
                  >
                    {o.refunded_cents > 0 ? formatCents(o.refunded_cents, o.currency) : "-"}
                  </td>
                  <td
                    className={cn(
                      "whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums",
                      o.surcharge_cents > 0 ? "text-foreground" : "text-faint",
                    )}
                  >
                    {o.surcharge_cents > 0 ? formatCents(o.surcharge_cents, o.currency) : "-"}
                  </td>
                  <td className="px-3 py-3">
                    <PaymentTypeChips types={o.payment_types ?? []} locationId={locationId} />
                  </td>
                  {showChannel && (
                    <td className="px-3 py-3">
                      <ChannelBadge channel={o.channel} />
                    </td>
                  )}
                  <td className="px-3 py-3 text-center">
                    <OrderStatusBadge status={o.status} label={t(orderStatusLabelKey(o.status))} />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right sm:px-6">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(o.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                        }
                      }}
                      aria-expanded={isExpanded}
                      aria-label={t(
                        isExpanded ? "transactions.collapseDetails" : "transactions.expandDetails",
                      )}
                      className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted transition-colors",
                        "hover:bg-surface-muted hover:text-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
                        isExpanded && "text-brand-primary",
                      )}
                    >
                      <ChevronRight
                        className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-90")}
                        aria-hidden="true"
                      />
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className={cn(i % 2 === 1 && "bg-surface-muted/40")}>
                    <td colSpan={showChannel ? 13 : 12} className="px-5 py-0 sm:px-6">
                      <OrderDetailView orderId={o.id} listOrder={o} mode="inline" />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Clickable column header with a sort-direction chevron. The
 *  un-sorted state shows a muted up/down indicator (so the affordance
 *  is visible at rest); the active column shows a brand-primary
 *  arrow in the current direction. Server-side sort means clicking
 *  a header re-fetches the page - debounce isn't necessary because
 *  each click toggles a single state value. */
function SortableHeader(props: {
  readonly column: OrderListSortBy;
  readonly label: string;
  readonly sortBy: OrderListSortBy;
  readonly sortDir: SortDir;
  readonly onSort: (col: OrderListSortBy) => void;
  readonly align?: "left" | "right";
  readonly className?: string;
}) {
  const isActive = props.sortBy === props.column;
  const ariaSort = isActive ? (props.sortDir === "asc" ? "ascending" : "descending") : "none";
  const Icon = !isActive ? ChevronsUpDown : props.sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(props.align === "right" && "text-right", props.className)}
    >
      <button
        type="button"
        onClick={() => props.onSort(props.column)}
        className={cn(
          "inline-flex min-h-tap items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider transition-colors",
          props.align === "right" && "flex-row-reverse",
          isActive
            ? "text-brand-primary hover:text-brand-primary/80"
            : "text-muted hover:text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:rounded-sm",
        )}
      >
        {props.label}
        <Icon className={cn("h-3 w-3 shrink-0", !isActive && "text-faint")} aria-hidden="true" />
      </button>
    </th>
  );
}

/** Per-order payment-method badge row - one chip per APPROVED charge
 *  payment_type the order saw. Empty list (open / no payments yet)
 *  renders the muted em-dash so the column never collapses. The
 *  small icon next to each chip lets the cashier scan the column at
 *  a glance without reading text. */
function PaymentTypeChips({
  types,
  locationId,
}: {
  readonly types: ReadonlyArray<string>;
  readonly locationId: string | null;
}) {
  if (types.length === 0) {
    return <span className="text-faint">-</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {types.map((t) => (
        <PaymentTypeChip key={t} type={t} locationId={locationId} />
      ))}
    </span>
  );
}

const CHIP_COLOR_MAP: Record<string, string> = {
  emerald:
    "bg-emerald-100 text-emerald-800 ring-emerald-300 dark:bg-emerald-900/60 dark:text-emerald-300 dark:ring-emerald-700",
  sky: "bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-900/60 dark:text-sky-300 dark:ring-sky-700",
  violet:
    "bg-violet-100 text-violet-800 ring-violet-300 dark:bg-violet-900/60 dark:text-violet-300 dark:ring-violet-700",
  amber:
    "bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-900/60 dark:text-amber-300 dark:ring-amber-700",
  neutral: "bg-surface-muted text-foreground ring-line",
};

function PaymentTypeChip({
  type,
  locationId,
}: {
  readonly type: string;
  readonly locationId: string | null;
}) {
  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = railsQuery.data?.rails ?? [];
  const rail =
    lookupMethodByTypeSubtype(rails, type, null) ??
    rails.find((r) => r.payment_type === type) ??
    null;

  const isDark = useDarkMode();
  const resolvedIcon = rail ? resolveMethodIcon(rail, isDark) : MinusCircle;
  const colorClass = rail
    ? rail.custom_color
      ? ""
      : (CHIP_COLOR_MAP[rail.color_token] ?? CHIP_COLOR_MAP.neutral)
    : CHIP_COLOR_MAP.neutral;
  const chipStyle = rail?.custom_color
    ? {
        backgroundColor: rail.custom_color + "26",
        color: rail.custom_color,
        outlineColor: rail.custom_color + "4d",
      }
    : undefined;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold capitalize ring-1",
        colorClass,
      )}
      style={chipStyle}
    >
      {typeof resolvedIcon === "string" ? (
        <img src={resolvedIcon} className="h-3 w-3" alt="" aria-hidden={true} />
      ) : (
        (() => {
          const Icon = resolvedIcon;
          return <Icon className="h-3 w-3" aria-hidden={true} />;
        })()
      )}
      {type}
    </span>
  );
}

const CHANNEL_CONFIG: Record<OrderChannel, { icon: React.ElementType; className: string }> = {
  pos: {
    icon: Monitor,
    className:
      "bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-900/60 dark:text-sky-300 dark:ring-sky-700",
  },
  kiosk: {
    icon: Computer,
    className:
      "bg-violet-100 text-violet-800 ring-violet-300 dark:bg-violet-900/60 dark:text-violet-300 dark:ring-violet-700",
  },
  m2m: {
    icon: Wifi,
    className:
      "bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-900/60 dark:text-amber-300 dark:ring-amber-700",
  },
  online: {
    icon: Globe,
    className:
      "bg-emerald-100 text-emerald-800 ring-emerald-300 dark:bg-emerald-900/60 dark:text-emerald-300 dark:ring-emerald-700",
  },
};

function ChannelBadge({ channel }: { readonly channel: OrderChannel }) {
  const { t } = useTranslation();
  const { icon: Icon, className: colorClass } = CHANNEL_CONFIG[channel] ?? CHANNEL_CONFIG.pos;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ring-1",
        colorClass,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {t(`transactions.channel.${channel}`)}
    </span>
  );
}

/** Flat list of all PENDING / ACTION_REQUIRED charge payments across orders.
 *  Auto-refreshes every 10 s so the cashier sees status transitions without
 *  manual refresh. Row click opens TimelinePaymentDrawer with cancel support. */
function PendingPaymentsView({
  locationIds,
  activeLocationId,
  scopeKind,
}: {
  readonly locationIds: ReadonlyArray<string>;
  readonly activeLocationId: string | null;
  readonly scopeKind: string;
}) {
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  const queryClient = useQueryClient();
  const [drawerPayment, setDrawerPayment] = useState<PendingPaymentRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{ orderId: string; paymentId: string } | null>(
    null,
  );

  const effectiveLocationIds =
    locationIds.length > 0
      ? locationIds
      : activeLocationId && scopeKind === "location"
        ? [activeLocationId]
        : [];

  const pending = useQuery({
    queryKey: ["pending-payments", { locationIds: [...effectiveLocationIds].sort().join(",") }],
    queryFn: () =>
      getPosClient().listPendingPayments(
        effectiveLocationIds.length > 0 ? { location_ids: effectiveLocationIds } : {},
      ),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const payments: readonly PendingPaymentRow[] = pending.data?.payments ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">
          {pending.data ? t("transactions.pendingCount", { count: pending.data.total }) : null}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => pending.refetch()}
          disabled={pending.isFetching}
          leftIcon={
            pending.isFetching ? (
              <InlineSpinner />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            )
          }
        >
          {t("transactions.refresh")}
        </Button>
      </div>

      <section
        className="overflow-hidden rounded-xl bg-surface shadow-elev-1"
        aria-label={t("transactions.tabPending")}
      >
        {pending.isPending && !pending.data ? (
          <div className="px-5 py-8">
            <PageSpinner />
          </div>
        ) : pending.isError ? (
          <div className="px-5 py-5">
            <Alert tone="error">{t("transactions.loadError")}</Alert>
          </div>
        ) : payments.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              icon={<Clock className="h-8 w-8" />}
              title={t("transactions.pendingEmptyTitle")}
              body={t("transactions.pendingEmptyBody")}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                <tr>
                  <th scope="col" className="px-5 py-3 sm:px-6">
                    {t("transactions.colTime")}
                  </th>
                  <th scope="col" className="px-3 py-3">
                    {t("transactions.colReference")}
                  </th>
                  <th scope="col" className="px-3 py-3">
                    {t("transactions.colCustomer")}
                  </th>
                  <th scope="col" className="px-3 py-3">
                    {t("transactions.colPayment")}
                  </th>
                  <th scope="col" className="px-3 py-3 text-right">
                    {t("transactions.colAmount")}
                  </th>
                  <th scope="col" className="px-3 py-3 text-center">
                    {t("transactions.colStatus")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {payments.map((p, i) => (
                  <tr
                    key={p.payment_id}
                    onClick={() => setDrawerPayment(p)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDrawerPayment(p);
                      }
                    }}
                    tabIndex={0}
                    aria-label={t("transactions.pendingRowAria", {
                      reference: p.order_external_reference ?? p.order_id,
                      customer: p.customer_name ?? "-",
                    })}
                    className={cn(
                      "cursor-pointer transition-colors",
                      i % 2 === 1 && "bg-surface-muted/40",
                      "hover:bg-surface-muted",
                      "focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
                    )}
                  >
                    <td className="whitespace-nowrap px-5 py-3 font-mono text-xs tabular-nums text-foreground sm:px-6">
                      {formatDateTime(p.created_at, tz)}
                    </td>
                    <td className="px-3 py-3 font-mono text-sm font-medium text-strong">
                      {p.order_external_reference ??
                        `#${p.order_id.split("-")[0]?.toUpperCase() ?? ""}`}
                    </td>
                    <td className="px-3 py-3 text-foreground">
                      <div>{p.customer_name ?? <span className="text-faint">-</span>}</div>
                      {p.customer_phone && (
                        <div className="font-mono text-xs text-muted">{p.customer_phone}</div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {p.payment_type ? (
                        <PaymentTypeChip type={p.payment_type} locationId={activeLocationId} />
                      ) : (
                        <span className="text-faint">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-mono font-semibold tabular-nums text-strong">
                      {formatCents(p.requested_amount_cents ?? p.applied_amount_cents, p.currency)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <PaymentStatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {drawerPayment && (
        <TimelinePaymentDrawer
          payment={drawerPayment}
          currency={drawerPayment.currency}
          orderId={drawerPayment.order_id}
          customerPhone={drawerPayment.customer_phone}
          locationId={activeLocationId}
          onClose={() => setDrawerPayment(null)}
          onCancelPayment={(paymentId) => {
            setCancelTarget({ orderId: drawerPayment.order_id, paymentId });
            setDrawerPayment(null);
          }}
        />
      )}

      {cancelTarget && (
        <CancelPaymentDialog
          orderId={cancelTarget.orderId}
          paymentId={cancelTarget.paymentId}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => {
            setCancelTarget(null);
            void queryClient.invalidateQueries({ queryKey: ["pending-payments"] });
            void pending.refetch();
          }}
        />
      )}
    </div>
  );
}
